import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Search } from 'lucide-react';
import Input from '../Input';
import './searchInput.css';

/* SearchInput — the browse rail's search field (`.br-search`).

   `type="search"`, not `type="text"`: it is what tells a screen reader what the
   field is for and what gives a phone keyboard its Search key. The UA's own
   clear button is suppressed in input.css because it collides with the glyph.

   Escape is stopped here and does not clear by default. Under 1025px the rail is
   a drawer that closes on Escape, and a shopper typing means "dismiss what I am
   typing", so the keypress never reaches the drawer. What it does beyond that is
   the caller's — `/all-products` dismisses its autocomplete suggestion and keeps
   the query — which is why clearing is opt-in via `clearOnEscape`. */
const SearchInput = forwardRef(function SearchInput({
  value,
  onChange,
  onClear,
  clearOnEscape,
  placeholder,
  label,
  className,
  ...rest
}, ref) {
  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && value) {
      e.stopPropagation();
      if (clearOnEscape) {
        if (onClear) onClear();
        else if (onChange) onChange('');
      }
    }
    if (rest.onKeyDown) rest.onKeyDown(e);
  };

  return (
    <div className={`br-search${className ? ` ${className}` : ''}`}>
      <Input
        ref={ref}
        type="search"
        size="lg"
        icon={Search}
        label={label || placeholder}
        hideLabel
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => onChange && onChange(e.target.value)}
        {...rest}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
});

SearchInput.propTypes = {
  value: PropTypes.string,
  /** Called with the new string, not the event. */
  onChange: PropTypes.func,
  /** How to clear, when `clearOnEscape` is set and '' is not enough. */
  onClear: PropTypes.func,
  /** Escape empties the field. Off by default — see the note above. */
  clearOnEscape: PropTypes.bool,
  placeholder: PropTypes.string,
  /** Accessible name, when it should differ from the placeholder. */
  label: PropTypes.string,
  className: PropTypes.string,
};

SearchInput.defaultProps = { value: '', placeholder: 'Search' };

export default SearchInput;
